import { Component, computed, effect, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DirectoryAccessService } from '@/services/app/directory-access.service';
import { DirectoryService } from '@/services/api/directory.service';
import { RolesService } from '@/services/api/roles.service';
import { UserRolesEditorService } from './user-roles-editor.service';
import { PermRole, UserRole, RemoveRole } from '@/models/user-data.model';
import { AccordionModule } from 'primeng/accordion';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { OfficesService } from '@/services/api/franchises.service';

interface GroupedRoles {
  franchiseName: string;
  franchiseId: number | null;
  roles: PermRole[];
}

@Component({
    selector: 'app-perms-list',
    imports: [
      CommonModule,
      AccordionModule,
      PanelModule,
      ButtonModule,
      TagModule,
      ChipModule,
      TooltipModule,
    ],
    template: `
      <div class="flex h-full">
        <!-- Available Roles -->
        <div class="flex-1 pr-4">
          <h3 class="text-lg font-semibold mb-3">Available Roles</h3>
          @if (groupedAvailableRoles().length > 0) {
            <p-accordion [multiple]="true" [activeIndex]="availableRolesActiveIndices()">
              @for (group of groupedAvailableRoles(); track group.franchiseId) {
                <p-accordionTab [header]="group.franchiseName">
                  <div class="flex flex-col gap-2">
                    @for (role of group.roles; track role.PermRoleID) {
                      <div class="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                        <div class="flex flex-col">
                          <span class="font-medium">{{ role.RoleName }}</span>
                          @if (role.jobTags && role.jobTags.length > 0) {
                            <div class="flex gap-1 mt-1">
                              @for (tag of role.jobTags; track tag.id) {
                                <p-chip [label]="tag.name" styleClass="text-xs" />
                              }
                            </div>
                          }
                        </div>
                        <p-button 
                          icon="pi pi-plus" 
                          [rounded]="true" 
                          [text]="true"
                          [severity]="'success'"
                          (onClick)="addRole(role)"
                          [disabled]="isRoleAssigned(role.PermRoleID)"
                          pTooltip="Add role to user"
                        />
                      </div>
                    }
                  </div>
                </p-accordionTab>
              }
            </p-accordion>
          } @else {
            <p class="text-gray-500 text-center py-4">No available roles</p>
          }
        </div>

        <!-- Vertical Divider -->
        <div class="w-px bg-gray-300"></div>

        <!-- Assigned Roles -->
        <div class="flex-1 pl-4">
          <h3 class="text-lg font-semibold mb-3">Assigned Roles</h3>
          @if (groupedAssignedRoles().length > 0) {
            <p-accordion [multiple]="true" [activeIndex]="assignedRolesActiveIndices()">
              @for (group of groupedAssignedRoles(); track group.franchiseId) {
                <p-accordionTab [header]="group.franchiseName">
                  <div class="flex flex-col gap-2">
                    @for (userRole of group.userRoles; track userRole.UserRoleID) {
                      <div class="flex items-center justify-between p-2 rounded hover:bg-gray-50">
                        <div class="flex flex-col">
                          <span class="font-medium">{{ userRole.PermRoles.RoleName }}</span>
                          @if (userRole.PermRoles.jobTags && userRole.PermRoles.jobTags.length > 0) {
                            <div class="flex gap-1 mt-1">
                              @for (tag of userRole.PermRoles.jobTags; track tag.id) {
                                <p-chip [label]="tag.name" styleClass="text-xs" />
                              }
                            </div>
                          }
                          @if (userRole.inviteRole) {
                            <p-tag severity="info" value="Invite Role" styleClass="text-xs mt-1" />
                          }
                        </div>
                        @if (isRemovable(userRole)) {
                        <p-button 
                          icon="pi pi-minus" 
                          [rounded]="true" 

                          [text]="true"
                          [severity]="'danger'"
                          (onClick)="removeRole(userRole)"
                          [disabled]="userRole.inviteRole"
                          pTooltip="Remove role from user"
                        />
                        }
                      </div>
                    }
                  </div>
                </p-accordionTab>
              }
            </p-accordion>
          } @else {
            <p class="text-gray-500 text-center py-4">No assigned roles</p>
          }
        </div>
      </div>
    `,
    styles: `
      :host {
        display: block;
        height: 100%;
      }
    `
})
export class PermsListComponent {
  directoryAccessService = inject(DirectoryAccessService);
  directoryService = inject(DirectoryService);
  rolesService = inject(RolesService);
  userRolesEditorService = inject(UserRolesEditorService);
  officesService = inject(OfficesService);

  // Get current user roles (original + edits)
  currentUserRoles = computed(() => {
    const selectedUserRoles = this.directoryService.rolesList();
    const editedRoles = this.userRolesEditorService.selectedUserRoles();
    
    if (!selectedUserRoles) return [];
    
    // Start with original user roles
    const originalRoles = selectedUserRoles || [];
    
    // Apply edits from the editor service
    // For now, we'll use the original roles and let the editor service track additions
    // The save component will handle merging these
    return editedRoles.length > 0 ? editedRoles : originalRoles;
  });

  // Filter roles that can be assigned based on permissions
  filteredAssignableRoles = computed(() => {
    const allowedOffices = this.directoryAccessService.allowedOffices();
    const roles = this.rolesService.roles();
    
    if (this.directoryAccessService.globalUnlocked()) {
      return roles;
    } else {
      return roles.filter(r => r.FID !== null && allowedOffices.includes(r.FID));
    }
  });

  // Available roles (not yet assigned)
  unassignedRoles = computed(() => {
    const assignable = this.filteredAssignableRoles();
    const assignedRoleIds = new Set(this.currentUserRoles().map(ur => ur.PermRoleID));
    return assignable.filter(r => !assignedRoleIds.has(r.PermRoleID));
  });

  // Group available roles by franchise
  groupedAvailableRoles = computed(() => {
    const roles = this.unassignedRoles();
    return this.groupRolesByFranchise(roles);
  });

  // Group assigned roles by franchise
  groupedAssignedRoles = computed(() => {
    const userRoles = this.currentUserRoles();
    return this.groupUserRolesByFranchise(userRoles);
  });

  // Indices for expanding all available role groups
  availableRolesActiveIndices = signal<number[]>([]);

  // Indices for expanding all assigned role groups
  assignedRolesActiveIndices = signal<number[]>([]);

  get selectedUser() {
    return this.directoryService.selectedUser();
  }

  isRemovable(userRole: UserRole): boolean {
    if(userRole.PermRoles.location) {
      return false;
    }
    return this.directoryAccessService.allowedOffices().includes(userRole.PermRoles.FID ?? 0);
  }

  constructor() {
    // Track the last user ID to detect user changes
    let lastUserId: number | null = null;
    
    // Sync user roles to editor service when selected user changes
    effect(() => {
      const selectedUser = this.directoryService.selectedUser();
      const selectedUserRoles = this.directoryService.rolesList().filter(r => r.PermRoles.location !== true);
      
      // Only update if we're switching users (not when roles are edited)
      const currentUserId = selectedUser?.UID ?? null;
      const isUserChanged = currentUserId !== lastUserId;
      
      if (selectedUserRoles && selectedUser) {
        // Only update if we're switching users, not when roles are edited
        if (isUserChanged) {
          this.userRolesEditorService.selectedUserRoles.set(selectedUser.UserRoles.filter(r => r.PermRoles.location !== true));
          // Clear removed roles when switching users
          this.userRolesEditorService.removedRoles.set([]);
          lastUserId = currentUserId;
        }
      } else {
        // Clear when no user is selected
        if (isUserChanged) {
          this.userRolesEditorService.selectedUserRoles.set([]);
          this.userRolesEditorService.removedRoles.set([]);
          lastUserId = null;
        }
      }
    });

    // Update available roles active indices asynchronously to avoid ExpressionChangedAfterItHasBeenCheckedError
    effect(() => {
      const groups = this.groupedAvailableRoles();
      // Defer update to next tick to avoid change detection issues
      setTimeout(() => {
        this.availableRolesActiveIndices.set(groups.map((_, index) => index));
      }, 0);
    });

    // Update assigned roles active indices asynchronously to avoid ExpressionChangedAfterItHasBeenCheckedError
    effect(() => {
      const groups = this.groupedAssignedRoles();
      // Defer update to next tick to avoid change detection issues
      setTimeout(() => {
        // Only expand groups that have at least one removable role
        const indicesToExpand = groups
          .map((group, index) => ({ group, index }))
          .filter(({ group }) => group.userRoles.some(userRole => this.isRemovable(userRole)))
          .map(({ index }) => index);
        this.assignedRolesActiveIndices.set(indicesToExpand);
      }, 0);
    });
  }

  private groupRolesByFranchise(roles: PermRole[]): GroupedRoles[] {
    const grouped = new Map<number | null, PermRole[]>();
    
    roles.forEach(role => {
      const fid = role.FID;
      if (!grouped.has(fid)) {
        grouped.set(fid, []);
      }
      grouped.get(fid)!.push(role);
    });

    return Array.from(grouped.entries()).map(([fid, roles]) => ({
      franchiseId: fid,
      franchiseName: this.getFranchiseName(fid),
      roles: roles.sort((a, b) => a.RoleName.localeCompare(b.RoleName))
    })).sort((a, b) => {
      // Sort null (global) last
      if (a.franchiseId === null) return 1;
      if (b.franchiseId === null) return -1;
      return a.franchiseName.localeCompare(b.franchiseName);
    });
  }

  private groupUserRolesByFranchise(userRoles: UserRole[]): Array<{ franchiseId: number | null; franchiseName: string; userRoles: UserRole[] }> {
    const grouped = new Map<number | null, UserRole[]>();
    
    userRoles.forEach(userRole => {
      const fid = userRole.PermRoles?.FID ?? null;
      if (!grouped.has(fid)) {
        grouped.set(fid, []);
      }
      grouped.get(fid)!.push(userRole);
    });

    return Array.from(grouped.entries()).map(([fid, userRoles]) => ({
      franchiseId: fid,
      franchiseName: this.getFranchiseName(fid),
      userRoles: userRoles.sort((a, b) => {
        const aName = a.PermRoles?.RoleName || '';
        const bName = b.PermRoles?.RoleName || '';
        return aName.localeCompare(bName);
      })
    })).sort((a, b) => {
      // Sort null (global) last
      if (a.franchiseId === null) return 1;
      if (b.franchiseId === null) return -1;
      return a.franchiseName.localeCompare(b.franchiseName);
    });
  }

  private getFranchiseName(fid: number | null): string {
    if (fid === null) {
      return 'Global';
    }
    
    // Try to get from PermRole's Franchises property first
    const role = this.rolesService.roles().find(r => r.FID === fid);
    if (role?.Franchises?.Title) {
      return role.Franchises.Title;
    }
    
    // Fallback to OfficesService
    const franchise = this.officesService.lookupFranchiseById(fid);
    return franchise?.Title || `Franchise ${fid}`;
  }

  isRoleAssigned(permRoleId: number): boolean {
    return this.currentUserRoles().some(ur => ur.PermRoleID === permRoleId);
  }

  addRole(role: PermRole) {
    const selectedUser = this.directoryService.selectedUser();
    if (!selectedUser) return;

    // Remove from removedRoles array if it was previously removed
    const currentRemovedRoles = this.userRolesEditorService.removedRoles();
    const updatedRemovedRoles = currentRemovedRoles.filter(
      rr => !(rr.PermRoleID === role.PermRoleID && rr.FID === role.FID)
    );
    this.userRolesEditorService.removedRoles.set(updatedRemovedRoles);

    // Create a new UserRole object
    const newUserRole: UserRole = {
      UID: selectedUser.UID,
      PermRoleID: role.PermRoleID,
      UserRoleID: 0, // Will be assigned by backend
      PermRoles: role,
      inviteRole: false
    };

    // Add to the editor service
    const currentRoles = this.userRolesEditorService.selectedUserRoles();
    this.userRolesEditorService.selectedUserRoles.set([...currentRoles, newUserRole]);
  }

  removeRole(userRole: UserRole) {
    // Don't allow removing invite roles
    if (userRole.inviteRole) return;

    // If the role has a UserRoleID > 0, it exists on the backend and needs to be tracked for removal
    if (userRole.UserRoleID > 0) {
      const removeRole: RemoveRole = {
        UserRoleID: userRole.UserRoleID,
        PermRoleID: userRole.PermRoleID,
        FID: userRole.PermRoles?.FID ?? null
      };
      
      // Add to removedRoles array if not already present
      const currentRemovedRoles = this.userRolesEditorService.removedRoles();
      const alreadyRemoved = currentRemovedRoles.some(
        rr => rr.UserRoleID === removeRole.UserRoleID
      );
      
      if (!alreadyRemoved) {
        this.userRolesEditorService.removedRoles.set([...currentRemovedRoles, removeRole]);
      }
    }

    const currentRoles = this.userRolesEditorService.selectedUserRoles();
    this.userRolesEditorService.selectedUserRoles.set(
      currentRoles.filter(ur => {
        // For existing roles, match by UserRoleID
        if (userRole.UserRoleID > 0) {
          return ur.UserRoleID !== userRole.UserRoleID;
        }
        // For new roles (UserRoleID = 0), match by PermRoleID and UID
        return !(ur.PermRoleID === userRole.PermRoleID && ur.UID === userRole.UID);
      })
    );
  }
}
